import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { GoogleSignInButton } from '@/features/auth/GoogleSignInButton';
import { useLoginMutation } from '@/features/auth/useAuthMutations';
import { loginSchema, type LoginInput } from '@/lib/zodSchemas/auth';
import { resolveNextRoute } from './resolveNextRoute';

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get('next');
  const mutation = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const data = await mutation.mutateAsync(values);
    navigate(resolveNextRoute(data.user, nextPath), { replace: true });
  });

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold">Entrar</h1>
      <p className="mt-2 text-sm text-(--color-fg-muted)">
        Acesse sua conta Organiza.AI.
      </p>

      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4" noValidate>
        <FormField id="email" label="E-mail" error={errors.email?.message} required>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            invalid={!!errors.email}
            {...register('email')}
          />
        </FormField>
        <FormField id="password" label="Senha" error={errors.password?.message} required>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            invalid={!!errors.password}
            {...register('password')}
          />
        </FormField>
        {mutation.isError ? (
          <p className="text-sm text-(--color-error)" role="alert">
            Não foi possível entrar. Verifique suas credenciais.
          </p>
        ) : null}
        <Button type="submit" disabled={isSubmitting || mutation.isPending}>
          {mutation.isPending ? 'Entrando…' : 'Entrar'}
        </Button>
      </form>

      <div className="mt-4 flex items-center gap-3 text-xs text-(--color-fg-muted)">
        <span className="h-px flex-1 bg-(--color-border)" /> ou <span className="h-px flex-1 bg-(--color-border)" />
      </div>

      <GoogleSignInButton nextPath={nextPath} />

      <div className="mt-6 flex flex-col gap-2 text-sm">
        <Link to="/forgot" className="text-(--color-accent) hover:underline">
          Esqueci minha senha
        </Link>
        <p className="text-(--color-fg-muted)">
          Não tem conta?{' '}
          <Link to="/signup" className="text-(--color-accent) hover:underline">
            Criar conta
          </Link>
        </p>
      </div>
    </div>
  );
}
